
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, RefreshCw, ChevronLeft, ChevronRight, Check, 
  AlertCircle, Download, FileText, ArrowRight, X, Package
} from '../components/Icons';
import { Card, Table, Thead, Tbody, Tr, Th, Td, PageHeader, Button } from '../components/UI';
import { DocumentPreview } from '../components/DocumentPreview';
import { useTranslation } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

interface OrderRequestItem {
  id: string;
  name: string;
  qty: number;
  price: number;
}

interface OrderRequest {
  id: string;
  requestNumber: string;
  date: string;
  customer: string;
  amount: number;
  status: 'NEW' | 'ORDERED' | 'REJECTED';
  items: OrderRequestItem[];
}

const mockRequests: OrderRequest[] = [
  { 
    id: '1', 
    requestNumber: 'REQ-2025-042', 
    date: '25.10.2025', 
    customer: 'VMI Services AG', 
    amount: 1450.00, 
    status: 'NEW',
    items: [
      { id: 'ITM-001-A', name: 'Industrial Ball Bearing X200', qty: 50, price: 25.00 },
      { id: 'ITM-009-I', name: 'Conveyor Belt segment', qty: 2, price: 100.00 }
    ]
  },
  { 
    id: '2', 
    requestNumber: 'REQ-2025-041', 
    date: '24.10.2025', 
    customer: 'VMI Services AG', 
    amount: 890.50, 
    status: 'NEW',
    items: [
      { id: 'ITM-002-B', name: 'Hydraulic Seal 50mm', qty: 100, price: 8.90 }
    ]
  },
  { 
    id: '3', 
    requestNumber: 'REQ-2025-038', 
    date: '22.10.2025', 
    customer: 'Global Logistics Hub', 
    amount: 5600.00, 
    status: 'ORDERED',
    items: [
      { id: 'ITM-010-J', name: 'Packing Foam Roll', qty: 20, price: 280.00 }
    ]
  },
  { 
    id: '4', 
    requestNumber: 'REQ-2025-035', 
    date: '20.10.2025', 
    customer: 'VMI Services AG', 
    amount: 210.00, 
    status: 'ORDERED',
    items: [
      { id: 'ITM-003-C', name: 'Control Module V4', qty: 1, price: 210.00 }
    ]
  },
  { 
    id: '5', 
    requestNumber: 'REQ-2025-030', 
    date: '15.10.2025', 
    customer: 'Swiss Retail Solutions', 
    amount: 12500.00, 
    status: 'REJECTED',
    items: [
      { id: 'ITM-004-D', name: 'Steel Fastener M12', qty: 5000, price: 2.50 }
    ]
  },
];

const SupplierOrderRequests: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');
  const [globalSearch, setGlobalSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [viewingRequest, setViewingRequest] = useState<OrderRequest | null>(null);

  const tabs = ['All', 'New', 'Ordered'];

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredData = useMemo(() => {
    return mockRequests.filter(req => {
      if (activeTab === 'New' && req.status !== 'NEW') return false;
      if (activeTab === 'Ordered' && req.status !== 'ORDERED') return false;

      if (globalSearch) {
        const s = globalSearch.toLowerCase();
        const matchesGlobal = req.requestNumber.toLowerCase().includes(s) || req.customer.toLowerCase().includes(s);
        if (!matchesGlobal) return false;
      }

      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        
        let itemValue = '';
        if (key === 'itemsCount') {
           itemValue = String(req.items.length);
        } else {
           itemValue = String((req as any)[key] || '').toLowerCase();
        }
        return itemValue.includes((value as string).toLowerCase());
      });
    });
  }, [activeTab, globalSearch, columnFilters]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'NEW':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-900/30">{t('new_request')}</span>;
      case 'ORDERED':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/30"><Check size={10} strokeWidth={4}/> {t('order_confirmed')}</span>;
      case 'REJECTED':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30"><X size={10} strokeWidth={4}/> {t('rejected')}</span>;
      default: return null;
    }
  };

  if (viewingRequest) {
    const doc = {
      orderNumber: viewingRequest.requestNumber,
      date: viewingRequest.date,
      customer: viewingRequest.customer,
      vendor: user?.companyName || 'Supplier AG',
      net: viewingRequest.amount,
      gross: viewingRequest.amount * 1.081,
      status: viewingRequest.status === 'ORDERED' ? 'Sent' : 'NEW',
      items: viewingRequest.items // Passing specific items to the detail view
    };
    return <DocumentPreview type="ORDER" document={doc} onBack={() => setViewingRequest(null)} />;
  }

  return (
    <div className="p-6 space-y-6 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={t('incoming_requests')}
        breadcrumbs={[{ label: user?.companyName || 'Supplier' }, { label: t('order_requests') }]}
        actions={
          <button className="h-9 px-4 flex items-center gap-2 bg-white dark:bg-[#262626] border border-gray-200 dark:border-[#393939] hover:bg-gray-50 text-gray-600 dark:text-gray-300 text-[10px] font-bold uppercase tracking-widest rounded-sm transition-all shadow-sm">
            <Download size={14} /> {t('batch_export')}
          </button>
        }
      />

      <Card className="overflow-hidden border-t-4 border-t-brand-medium">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-[#393939] bg-white dark:bg-[#161616] flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-1 bg-[#F5F7FA] dark:bg-[#262626] p-1 rounded-md">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`h-8 px-4 text-[10px] font-bold uppercase tracking-widest transition-all rounded-sm ${
                  activeTab === tab 
                    ? 'bg-white dark:bg-[#161616] text-brand-dark dark:text-white shadow-sm' 
                    : 'text-gray-500 hover:text-brand-dark dark:hover:text-white'
                }`}
              >
                {t(tab.toLowerCase() as any)}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
             <div className="relative group">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-medium transition-colors" />
                <input 
                  type="text" 
                  placeholder={t('search_requests')} 
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  className="h-9 pl-9 pr-4 bg-[#F5F7FA] dark:bg-[#262626] border border-transparent focus:border-brand-medium focus:bg-white dark:focus:bg-[#1F1F1F] rounded-sm text-xs w-64 outline-none transition-all"
                />
             </div>
             <button className="h-9 w-9 flex items-center justify-center bg-[#F5F7FA] dark:bg-[#262626] hover:bg-brand-medium hover:text-white text-gray-500 dark:text-gray-400 rounded-sm transition-colors shadow-sm">
                <RefreshCw size={14} />
             </button>
          </div>
        </div>

        <Table>
          <Thead>
            <Tr isHeader>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('request_number')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('requestNumber', e.target.value)}
                  />
                </div>
              </Th>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('customer')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('customer', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex flex-col gap-2 items-end">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('items_count')}</span>
                  <input 
                    className="w-20 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('itemsCount', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex flex-col gap-2 items-end">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('requested_amount')} (CHF)</span>
                  <input 
                    className="w-24 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('amount', e.target.value)}
                  />
                </div>
              </Th>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('status')}</span>
                  <select 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                    onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                  >
                    <option value="">{t('all')}</option>
                    <option value="NEW">{t('new')}</option>
                    <option value="ORDERED">{t('ordered')}</option>
                    <option value="REJECTED">{t('rejected')}</option>
                  </select>
                </div>
              </Th>
              <Th className="text-right">
                 {t('actions')}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredData.map((req) => (
              <Tr key={req.id} onClick={() => setViewingRequest(req)}>
                <Td>
                   <div className="flex flex-col">
                      <span className="font-bold text-brand-dark dark:text-white font-mono">{req.requestNumber}</span>
                      <span className="text-[10px] text-gray-400">{req.date}</span>
                   </div>
                </Td>
                <Td className="text-sm font-medium text-gray-700 dark:text-gray-300">{req.customer}</Td>
                <Td className="text-right">
                   <div className="flex items-center justify-end gap-2 text-xs text-gray-500">
                      <Package size={12} /> {req.items.length} {t('items')}
                   </div>
                </Td>
                <Td className="text-right font-black text-brand-dark dark:text-white">
                   {req.amount.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                </Td>
                <Td>{getStatusBadge(req.status)}</Td>
                <Td className="text-right">
                   <div className="flex items-center justify-end gap-2">
                      {req.status === 'NEW' && (
                         <Button size="sm" className="h-7 px-3 text-[9px] !bg-brand-dark hover:!bg-brand-medium" onClick={(e) => { e.stopPropagation(); alert('Processing request to confirmed order...'); req.status = 'ORDERED'; setActiveTab('Ordered'); }}>
                            {t('convert_to_order')}
                         </Button>
                      )}
                      <button className="p-2 text-gray-300 hover:text-brand-dark dark:hover:text-white" onClick={() => setViewingRequest(req)}>
                         <ArrowRight size={16} />
                      </button>
                   </div>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card>
    </div>
  );
};

export default SupplierOrderRequests;
